<script setup lang="ts">
import { ref, onMounted, onUnmounted } from "vue";
import { MemoryMonitor, type MemorySnapshot } from "@/utils/MemoryMonitor";

const snap = ref<MemorySnapshot | null>(null);
const drawingDelta = ref<number | null>(null);
const sessionDelta = ref<number | null>(null);
let timer: ReturnType<typeof setInterval> | null = null;

function refresh() {
  snap.value = MemoryMonitor.snapshot();
  const d = MemoryMonitor.delta();
  drawingDelta.value = d ? (d.wasmUsedDelta !== 0 ? d.wasmUsedDelta : d.jsHeapDelta) : null;
  sessionDelta.value = MemoryMonitor.sessionDelta();
}

onMounted(() => {
  refresh();
  timer = setInterval(refresh, 2000);
});

onUnmounted(() => {
  if (timer !== null) clearInterval(timer);
});

function mb(bytes: number) {
  return (bytes / 1024 / 1024).toFixed(1);
}

function deltaColor(bytes: number) {
  const m = bytes / 1024 / 1024;
  if (m > 100) return "#ff4444";
  if (m > 50) return "#ffa500";
  if (m > 10) return "#ffff00";
  return "#88ff88";
}
</script>

<template>
  <div v-if="snap" class="mem-overlay">
    <div v-if="snap.wasmUsed > 0" class="mem-row">
      <span class="mem-label">WASM used</span>
      <span class="mem-value">{{ mb(snap.wasmUsed) }} MB</span>
    </div>
    <div v-if="snap.wasmTotal > 0" class="mem-row">
      <span class="mem-label">WASM total</span>
      <span class="mem-value">{{ mb(snap.wasmTotal) }} MB</span>
    </div>
    <div v-if="snap.jsHeap > 0" class="mem-row">
      <span class="mem-label">Heap</span>
      <span class="mem-value">{{ mb(snap.jsHeap) }} MB</span>
    </div>
    <div v-if="drawingDelta !== null" class="mem-row">
      <span class="mem-label">Δ drawing</span>
      <span class="mem-value" :style="{ color: deltaColor(drawingDelta) }">
        {{ drawingDelta >= 0 ? "+" : "" }}{{ mb(drawingDelta) }} MB
      </span>
    </div>
    <div v-if="sessionDelta !== null" class="mem-row">
      <span class="mem-label">Δ session</span>
      <span class="mem-value" :style="{ color: deltaColor(sessionDelta) }">
        {{ sessionDelta >= 0 ? "+" : "" }}{{ mb(sessionDelta) }} MB
      </span>
    </div>
  </div>
</template>

<style scoped>
.mem-overlay {
  position: fixed;
  bottom: 12px;
  left: 12px;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.75);
  color: #fff;
  font-family: monospace;
  font-size: 11px;
  padding: 6px 10px;
  border-radius: 6px;
  pointer-events: none;
  min-width: 160px;
}
.mem-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  line-height: 1.6;
}
.mem-label {
  color: #aaa;
}
.mem-value {
  color: #88ff88;
  font-weight: bold;
}
</style>
